import { useCallback } from "react";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import { SdbProject } from "@custom-types/project-types";
import { UploadSettings, useUploadMultipleScans } from "@hooks/data-management/use-upload-multiple-scans";
import { UploadFilesErrorToastType, useUploadFilesTrackError } from "@hooks/data-management/use-upload-files-track-error";
import { cleanupUploadTasks } from "@pages/data-management/import-data/cancel-import-utils";
import { CreateRevision, createRevisionForLsDataV2 } from "@pages/data-management/import-data/create-revision-for-lsdatav2";
import { useAppSelector } from "@store/store-helper";
import { uploadTasksSelector } from "@store/upload-tasks/upload-tasks-selector";
import { LsDataV2FilesInfo } from "@pages/data-management/data-management-types";
import { FileUploadTaskContext } from "@custom-types/file-upload-types";
import { useActivityApiClient } from "@api/activity-api/use-activity-api-client";
import { useFileUpload } from "@hooks/use-file-upload";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { sentryCaptureError } from "@utils/sentry-utils";

type ReturnFunction = (
  /** Data provided by the associated file validation helper getLsDataV2FilesInfo. */
  fileInfos: LsDataV2FilesInfo,
  uploadSettings?: UploadSettings,
) => Promise<boolean>;

/**
 * Uploads LsDataV2 Projects with files in the info object provided by getLsDataV2FilesInfo.
 * @param project The project to upload the files to.
 */
export function useOnUploadLsDataV2Files(project: SdbProject): ReturnFunction {
  const projectId = project.id;
  const uploadTasks = useAppSelector(uploadTasksSelector);
  const companyId = useAppSelector(selectedSdbCompanyIdSelector) || undefined;
  const currentUser = useAppSelector(currentUserSelector);
  const uploadFilesTrackError = useUploadFilesTrackError();
  const { uploadManager } = useFileUploadContext();
  const { uploadSingleFile } = useFileUpload();
  const projectApiClient = useProjectApiClient({ projectId });
  const activityApiClient = useActivityApiClient({ companyId, projectId });
  const uploadMultipleScans = useUploadMultipleScans(project);
  const { handleErrorWithToast } = useErrorContext();

  return useCallback(
    async (fileInfos, uploadSettings): Promise<boolean> => {
      const {
        filesToUpload,
        lsDataV2,
        lsDataV2ZipFile,
        scansAlreadyUploaded,
        elementType,
      } = fileInfos;

      let revision: CreateRevision;
      try {
        revision = await createRevisionForLsDataV2(projectApiClient, lsDataV2, filesToUpload);
      } catch(error) {
        uploadFilesTrackError(UploadFilesErrorToastType.createRevision, elementType);
        // TODO(TF-2183): Show ErrorView, for UX consistency and to enable retry.
        handleErrorWithToast({
          id: `createRevisionForElsScans-${Date.now().toString()}`,
          title: "Failed to prepare scan upload",
          error,
        });
        return false;
      }

      // Optional (continue on error):
      // Upload the LsDataV2 zip file, and then send the url as debugging info to the activity api.
      if (lsDataV2ZipFile) {
        // `isSuccessful` is only for the upload, and does not include the onUploadComplete() callback.
        const isSuccessful = await uploadSingleFile({
          file: lsDataV2ZipFile,
          onUploadStart: () => { },
          onUploadProgress: (_progressEvent: ProgressEvent | number) => { },
          onUploadComplete: async (url: string) => {
            try {
              await activityApiClient.sendLsDataV2DebugInfo({
                userId: currentUser?.id,
                revisionId: revision.captureTreeRevisionId,
                url,
                elementType,
              });
            } catch (error) {
              sentryCaptureError({
                title: "Failed to send lsDataV2.zip to ActivityAPI",
                error,
              });
              uploadFilesTrackError(UploadFilesErrorToastType.uploadMetadataFiles, elementType);
            }
          },
          context: {} as FileUploadTaskContext,
          isSilentError: true,
        });

        if (!isSuccessful) {
          // The upload request has failed. This has been logged to Sentry already.
          // Track also to Amplitude, then continue, since it's debug info only.
          uploadFilesTrackError(UploadFilesErrorToastType.uploadMetadataFiles, elementType);
        }
      }

      // Cleaning up old upload tasks should be done after createRevisionForElsScans to minimize the time during
      // which the "old" state is active again before the state will be set to uploading.
      cleanupUploadTasks(uploadTasks, uploadManager);

      try {
        const {
          captureTreeRevisionId,
          captureTreeRootAndClustersByUuid,
          captureTreeMaps,
        } = revision;
        // Checking for existing revisions takes some time, so we "await" here to make sure that the spinner is shown.
        // The actual upload of the scans is done in the background.
        await uploadMultipleScans(captureTreeRevisionId, captureTreeRootAndClustersByUuid,
          captureTreeMaps.scanIdByUuid, filesToUpload, lsDataV2, scansAlreadyUploaded, elementType, uploadSettings);
        return true;
      } catch {
        uploadFilesTrackError(UploadFilesErrorToastType.uploadBlinkFiles, elementType);
        return false;
      }
    },
    [
      projectApiClient, activityApiClient, uploadTasks, uploadManager, currentUser,
      uploadFilesTrackError, uploadMultipleScans, uploadSingleFile, handleErrorWithToast,
    ]
  );
}
